"use client";

/**
 * P3-15 · Search overlay — the instant, as-you-type autocomplete (P3-DB04).
 *
 * A WAI-ARIA combobox with a listbox popup: the input keeps DOM focus at all
 * times and ↑/↓ move an `aria-activedescendant` highlight across a single flat
 * item list spanning every group (exact pin → products → categories → queries
 * → footer, or recent → featured when empty). Tab is inert; row actions are
 * pointer-operable and driven by combobox keys, never tab stops. Esc is
 * two-stage (clear highlight, then close). The debounced `search.suggest`
 * query keeps previous data so the list never blanks mid-type, and React
 * Query cancels superseded requests so only the latest query's answer shows.
 *
 * Results page (`/search?q=`) is P3-16; category/query/footer activation
 * routes there, products/exact route to the PDP (exact carries `?variant=`).
 */
import { keepPreviousData } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import * as React from "react";
import { createPortal } from "react-dom";

import { useLocaleHref, useTranslator } from "~/i18n/provider";
import { track as capture } from "~/lib/analytics/client";
import { useDebouncedValue } from "~/lib/hooks/use-debounced-value";
import { formatMoney } from "~/lib/money";
import { highlightMatch } from "~/lib/search/highlight";
import { useSearchHistory } from "~/lib/search/use-search-history";
import { categoryHref } from "~/lib/storefront/category-href";
import { cn } from "~/lib/utils";
import { StoreImage } from "~/components/ui/store-image";
import { api, type RouterOutputs } from "~/trpc/react";

type Suggestions = RouterOutputs["search"]["suggest"];

export interface FeaturedCategory {
  name: string;
  href: string;
}

const MIN_QUERY = 2;
const DEBOUNCE_MS = 120;
const LISTBOX_ID = "search-suggestions";

/** A single activatable row, flattened across groups for one keyboard model. */
type Item =
  | { kind: "exact"; hit: NonNullable<Suggestions["exact"]> }
  | { kind: "product"; product: Suggestions["products"][number] }
  | { kind: "category"; category: Suggestions["categories"][number] }
  | { kind: "query"; text: string }
  | { kind: "recent"; text: string }
  | { kind: "trending"; text: string }
  | { kind: "featured"; category: FeaturedCategory }
  | { kind: "footer"; text: string };

interface Section {
  label: string | null;
  rows: { item: Item; index: number; domId: string }[];
}

export function SearchOverlay({
  open,
  onClose,
  featuredCategories = [],
  historyEnabled = true,
  signedIn = false,
}: {
  open: boolean;
  onClose: () => void;
  featuredCategories?: FeaturedCategory[];
  historyEnabled?: boolean;
  /** Signed-in shoppers use account history + migrate their guest terms (P3-17). */
  signedIn?: boolean;
}) {
  const t = useTranslator();
  const router = useRouter();
  const localeHref = useLocaleHref();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [mounted, setMounted] = React.useState(false);
  const [q, setQ] = React.useState("");
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const {
    recent,
    add: addRecent,
    remove: removeRecent,
    clear: clearRecent,
  } = useSearchHistory({ signedIn, enabled: historyEnabled });
  const logEvent = api.search.logEvent.useMutation();

  React.useEffect(() => setMounted(true), []);

  const debouncedQ = useDebouncedValue(q, DEBOUNCE_MS);
  const trimmed = debouncedQ.trim();
  const enabled = open && trimmed.length >= MIN_QUERY;

  const suggest = api.search.suggest.useQuery(
    { q: trimmed },
    {
      enabled,
      placeholderData: keepPreviousData,
      staleTime: 30_000,
      retry: 1,
      refetchOnWindowFocus: false,
    },
  );

  const isTyping = q.trim().length >= MIN_QUERY;
  const data = enabled ? suggest.data : undefined;

  // Analytics-derived trending terms for the empty overlay (flag-gated server
  // returns [] when off). Fetched only while the empty launchpad is showing.
  const trending = api.search.trending.useQuery(
    { limit: 6 },
    { enabled: open && !isTyping, staleTime: 5 * 60_000 },
  );
  const trendingTerms = React.useMemo(
    () => trending.data ?? [],
    [trending.data],
  );

  // ── Scroll-lock + open/close focus (input keeps focus; Tab is trapped to
  //    it so focus never leaves the combobox, per §04). ──────────────────────
  React.useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    // Focus the input on the next frame (portal must be in the DOM first).
    const raf = requestAnimationFrame(() => inputRef.current?.focus());
    return () => {
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
    };
  }, [open]);

  // Reset on each open, and clear the highlight whenever the query changes.
  React.useEffect(() => {
    if (open) {
      setQ("");
      setActiveIndex(-1);
    }
  }, [open]);
  React.useEffect(() => setActiveIndex(-1), [debouncedQ]);

  // ── Build the flat, ordered item list + display sections. ─────────────────
  const sections = React.useMemo<Section[]>(() => {
    const built: { label: string | null; items: Item[] }[] = [];
    if (isTyping) {
      if (data?.exact)
        built.push({
          label: null,
          items: [{ kind: "exact", hit: data.exact }],
        });
      if (data && data.products.length > 0)
        built.push({
          label: "Products",
          items: data.products.map((product) => ({ kind: "product", product })),
        });
      if (data && data.categories.length > 0)
        built.push({
          label: "Categories",
          items: data.categories.map((category) => ({
            kind: "category",
            category,
          })),
        });
      if (data && data.queries.length > 0)
        built.push({
          label: "Suggested queries",
          items: data.queries.map((text) => ({ kind: "query", text })),
        });
      // The footer runs the full query — present whenever there's a query.
      built.push({
        label: null,
        items: [{ kind: "footer", text: q.trim() }],
      });
    } else {
      if (recent.length > 0)
        built.push({
          label: "Recent",
          items: recent.map((text) => ({ kind: "recent", text })),
        });
      if (trendingTerms.length > 0)
        built.push({
          label: "Trending",
          items: trendingTerms.map((text) => ({ kind: "trending", text })),
        });
      if (featuredCategories.length > 0)
        built.push({
          label: "Categories",
          items: featuredCategories.map((category) => ({
            kind: "featured",
            category,
          })),
        });
    }
    let index = 0;
    return built.map((s) => ({
      label: s.label,
      rows: s.items.map((item) => {
        const row = { item, index, domId: `search-opt-${index}` };
        index += 1;
        return row;
      }),
    }));
  }, [isTyping, data, recent, trendingTerms, featuredCategories, q]);

  const flatItems = React.useMemo(
    () => sections.flatMap((s) => s.rows),
    [sections],
  );

  const activeRow = flatItems.find((r) => r.index === activeIndex);
  const activeDomId = activeRow?.domId;

  // Keep the highlighted row in view during arrow navigation. Optional call:
  // scrollIntoView is unimplemented in jsdom and absent on some engines.
  React.useEffect(() => {
    if (activeDomId) {
      document
        .getElementById(activeDomId)
        ?.scrollIntoView?.({ block: "nearest" });
    }
  }, [activeDomId]);

  const goToResults = React.useCallback(
    (term: string) => {
      const value = term.trim();
      if (!value) return;
      addRecent(value);
      capture("SEARCH", { searchQuery: value });
      router.push(localeHref(`/search?q=${encodeURIComponent(value)}`));
      onClose();
    },
    [addRecent, router, onClose, localeHref],
  );

  const activate = React.useCallback(
    (item: Item, position: number) => {
      // Persist the click-through (term + rank + product) for P9 + ranking.
      const logClick = (productId: string) =>
        logEvent.mutate({
          type: "suggestion_click",
          term: q.trim(),
          position,
          productId,
        });
      switch (item.kind) {
        case "exact":
          addRecent(q.trim());
          logClick(item.hit.productId);
          router.push(
            localeHref(
              `/products/${item.hit.slug}?variant=${item.hit.variantId}`,
            ),
          );
          onClose();
          break;
        case "product":
          addRecent(q.trim());
          logClick(item.product.id);
          router.push(localeHref(`/products/${item.product.slug}`));
          onClose();
          break;
        case "category":
          router.push(localeHref(categoryHref(item.category.slug)));
          onClose();
          break;
        case "featured":
          router.push(localeHref(item.category.href));
          onClose();
          break;
        case "query":
        case "recent":
        case "trending":
          goToResults(item.text);
          break;
        case "footer":
          goToResults(item.text);
          break;
      }
    },
    [q, addRecent, router, onClose, goToResults, logEvent, localeHref],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const n = flatItems.length;
    switch (e.key) {
      case "ArrowDown":
        if (n === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % n);
        break;
      case "ArrowUp":
        if (n === 0) return;
        e.preventDefault();
        setActiveIndex((i) => (i <= 0 ? n - 1 : i - 1));
        break;
      case "Enter":
        e.preventDefault();
        if (activeRow) activate(activeRow.item, activeRow.index);
        else goToResults(q);
        break;
      case "Escape":
        e.preventDefault();
        // Two-stage: first clear the highlight, then close.
        if (activeIndex >= 0) setActiveIndex(-1);
        else onClose();
        break;
      case "Tab":
        // Inert — focus never leaves the input (combobox keeps the caret).
        e.preventDefault();
        break;
      case "Delete":
      case "Backspace":
        if (
          activeRow?.item.kind === "recent" &&
          (e.key === "Delete" || q.length === 0)
        ) {
          e.preventDefault();
          removeRecent(activeRow.item.text);
        }
        break;
    }
  };

  // ── aria-live: announce the shape of each settled answer. ─────────────────
  const liveMessage = React.useMemo(() => {
    if (!isTyping || !data) return "";
    const parts: string[] = [];
    if (data.exact) parts.push("1 exact match");
    if (data.products.length) parts.push(`${data.products.length} products`);
    if (data.categories.length)
      parts.push(`${data.categories.length} categories`);
    if (data.degraded) return "Product suggestions are temporarily unavailable. Open full search results.";
    if (parts.length === 0) return `No results for ${q.trim()}`;
    return parts.join(", ");
  }, [isTyping, data, q]);

  if (!mounted || !open) return null;

  const showLoading = isTyping && suggest.isFetching && !suggest.data;
  const showError = isTyping && suggest.isError;
  const showNoResults =
    isTyping &&
    !showError &&
    !suggest.isFetching &&
    !!data &&
    !data.degraded &&
    !data.exact &&
    data.products.length === 0 &&
    data.categories.length === 0;

  return createPortal(
    <div className="fixed inset-0 z-50">
      <div
        className="absolute inset-0 bg-black/40"
        aria-hidden
        onClick={onClose}
      />
      <div
        className={cn(
          "absolute inset-x-0 top-0 mx-auto flex h-full w-full flex-col bg-surface shadow-[var(--shadow-lg)]",
          // Desktop: a dropdown sheet under the header, not full height.
          "sm:h-auto sm:max-h-[70vh] sm:max-w-2xl sm:rounded-b-xl sm:border-x sm:border-b sm:border-border",
        )}
      >
        {/* Input row */}
        <div className="flex items-center gap-2 border-b border-border p-3">
          <svg
            aria-hidden="true"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            className="h-5 w-5 shrink-0 text-fg-muted"
          >
            <circle cx="11" cy="11" r="6.5" />
            <path d="m20 20-3.8-3.8" />
          </svg>
          <input
            ref={inputRef}
            type="search"
            role="combobox"
            aria-expanded="true"
            aria-controls={LISTBOX_ID}
            aria-autocomplete="list"
            aria-activedescendant={activeDomId}
            aria-label={t("search.searchProducts")}
            enterKeyHint="search"
            autoComplete="off"
            placeholder={t("search.searchProducts")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={onKeyDown}
            className="min-w-0 flex-1 bg-transparent text-base text-fg outline-none placeholder:text-fg-muted"
          />
          <button
            type="button"
            onClick={onClose}
            tabIndex={-1}
            className="shrink-0 rounded-md px-2 py-1 text-sm text-secondary hover:bg-surface-2"
          >
            <span className="hidden sm:inline">Esc</span>
            <span className="sm:hidden">{t("search.cancel")}</span>
          </button>
        </div>

        {isTyping && (
          <button type="button" onClick={() => goToResults(q)}
            className="px-4 py-3 text-left text-sm font-semibold underline">
            Open full search results
          </button>
        )}
        {/* Results region */}
        <div
          id={LISTBOX_ID}
          role="listbox"
          aria-label={t("search.suggestions")}
          className={cn(
            "flex-1 overflow-y-auto overscroll-contain p-2",
            suggest.isFetching && suggest.data && "opacity-60",
          )}
        >
          <div aria-live="polite" className="sr-only">
            {liveMessage}
          </div>

          {showLoading && <SkeletonRows />}
          {showError && <ErrorRetry onRetry={() => void suggest.refetch()} />}
          {showNoResults && (
            <NoResults
              query={q.trim()}
              featured={featuredCategories}
              onClear={() => {
                setQ("");
                inputRef.current?.focus();
              }}
              onPick={(href) => {
                router.push(localeHref(href));
                onClose();
              }}
            />
          )}

          {!showLoading &&
            !showError &&
            !showNoResults &&
            sections.map((section, si) => (
              <div
                key={si}
                role="group"
                aria-label={section.label ?? undefined}
              >
                {section.label && (
                  <div className="px-2 pb-1 pt-3 font-mono text-[10px] uppercase tracking-wider text-fg-muted">
                    {section.label}
                  </div>
                )}
                {section.rows.map((row) => (
                  <Row
                    key={row.domId}
                    row={row}
                    query={q}
                    active={row.index === activeIndex}
                    onActivate={() => activate(row.item, row.index)}
                    onHover={() => setActiveIndex(row.index)}
                    onRemoveRecent={
                      row.item.kind === "recent"
                        ? () =>
                            removeRecent((row.item as { text: string }).text)
                        : undefined
                    }
                  />
                ))}
                {section.label === "Recent" && (
                  <div className="flex justify-end px-2">
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={clearRecent}
                      className="rounded px-2 py-1 text-xs text-secondary hover:bg-surface-2"
                    >
                      {t("browse.clearAll")}
                    </button>
                  </div>
                )}
              </div>
            ))}

          {isTyping &&
            !showLoading &&
            !showError &&
            data?.degraded &&
            data.products.length === 0 && (
              <p className="px-2 py-2 text-xs text-fg-muted">
                {t("search.resultsUnavailable")}
              </p>
            )}
          {!isTyping &&
            recent.length === 0 &&
            trendingTerms.length === 0 &&
            featuredCategories.length === 0 && (
              <p className="px-3 py-6 text-center text-sm text-fg-muted">
                {t("search.startTyping")}
              </p>
            )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

// ── Row renderers ───────────────────────────────────────────────────────────

function Row({
  row,
  query,
  active,
  onActivate,
  onHover,
  onRemoveRecent,
}: {
  row: { item: Item; domId: string };
  query: string;
  active: boolean;
  onActivate: () => void;
  onHover: () => void;
  onRemoveRecent?: () => void;
}) {
  const { item, domId } = row;
  return (
    <div
      id={domId}
      role="option"
      aria-selected={active}
      onClick={onActivate}
      onMouseMove={onHover}
      className={cn(
        "flex cursor-pointer items-center gap-3 rounded-lg px-2 py-2",
        active && "bg-secondary/10 ring-1 ring-inset ring-secondary/30",
      )}
    >
      <RowContent item={item} query={query} />
      {onRemoveRecent && (
        <button
          type="button"
          tabIndex={-1}
          aria-label={`Remove recent search`}
          onClick={(e) => {
            e.stopPropagation();
            onRemoveRecent();
          }}
          className="shrink-0 rounded p-1 text-fg-muted hover:bg-surface-2"
        >
          ×
        </button>
      )}
    </div>
  );
}

function RowContent({ item, query }: { item: Item; query: string }) {
  switch (item.kind) {
    case "exact":
      return (
        <>
          <Thumb thumbnail={null} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium text-fg">
              {item.hit.title}
            </div>
            <div className="font-mono text-[11px] text-fg-muted">
              {item.hit.optionValues.join(" · ")} ·{" "}
              {formatMoney(item.hit.priceMinor, item.hit.currency)}
            </div>
          </div>
          <span className="shrink-0 rounded border border-border px-1.5 py-0.5 font-mono text-[10px] text-secondary">
            exact
          </span>
        </>
      );
    case "product":
      return (
        <>
          <Thumb thumbnail={item.product.thumbnail} />
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm text-fg">
              {highlightMatch(item.product.title, query)}
              {!item.product.inStock && (
                <span className="ml-2 text-xs text-fg-muted">
                  · out of stock
                </span>
              )}
            </div>
            {item.product.brand && (
              <div className="truncate font-mono text-[11px] text-fg-muted">
                {item.product.brand}
              </div>
            )}
          </div>
          <ProductPrice product={item.product} />
        </>
      );
    case "category":
      return (
        <>
          <span aria-hidden className="w-5 shrink-0 text-center text-fg-muted">
            ▦
          </span>
          <div className="min-w-0 flex-1 truncate text-sm text-fg">
            {highlightMatch(item.category.name, query)}
          </div>
          <span className="shrink-0 font-mono text-[11px] text-fg-muted">
            {item.category.productCount}
          </span>
        </>
      );
    case "query":
    case "recent":
    case "trending":
      return (
        <>
          <span aria-hidden className="w-5 shrink-0 text-center text-fg-muted">
            {item.kind === "recent"
              ? "↺"
              : item.kind === "trending"
                ? "↗"
                : "⌕"}
          </span>
          <div className="min-w-0 flex-1 truncate text-sm text-fg">
            {item.kind === "query"
              ? highlightMatch(item.text, query)
              : item.text}
          </div>
        </>
      );
    case "featured":
      return (
        <>
          <span aria-hidden className="w-5 shrink-0 text-center text-fg-muted">
            ▦
          </span>
          <div className="min-w-0 flex-1 truncate text-sm text-fg">
            {item.category.name}
          </div>
        </>
      );
    case "footer":
      return (
        <>
          <span aria-hidden className="w-5 shrink-0 text-center text-secondary">
            ⌕
          </span>
          <div className="min-w-0 flex-1 truncate text-sm font-medium text-secondary">
            See all results for “{item.text}”
          </div>
        </>
      );
  }
}

function ProductPrice({
  product,
}: {
  product: Suggestions["products"][number];
}) {
  if (product.priceMinor === null) return null;
  const currency = product.currency ?? "GBP";
  const onSale =
    product.compareAtMinor !== null &&
    product.compareAtMinor > product.priceMinor;
  return (
    <div className="shrink-0 text-right">
      <div
        className={cn(
          "text-sm font-semibold",
          onSale ? "text-accent" : "text-fg",
        )}
      >
        {formatMoney(product.priceMinor, currency)}
      </div>
      {onSale && (
        <div className="text-[11px] text-fg-muted line-through">
          {formatMoney(product.compareAtMinor!, currency)}
        </div>
      )}
    </div>
  );
}

function Thumb({
  thumbnail,
}: {
  thumbnail: Suggestions["products"][number]["thumbnail"];
}) {
  if (!thumbnail?.url) {
    return (
      <span
        aria-hidden
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-surface-2 text-fg-muted"
      >
        ▢
      </span>
    );
  }
  return (
    <span className="relative h-11 w-11 shrink-0 overflow-hidden rounded-md bg-surface-2">
      <StoreImage
        src={thumbnail.url}
        alt={thumbnail.alt ?? ""}
        preset="thumb"
        width={44}
        height={44}
        blurDataUrl={thumbnail.blurDataUrl}
        loading="lazy"
        className="h-full w-full object-cover"
      />
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="space-y-2 p-1" aria-hidden>
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-3 px-2 py-2">
          <span className="h-11 w-11 shrink-0 animate-pulse rounded-md bg-surface-2" />
          <span className="h-3 flex-1 animate-pulse rounded bg-surface-2" />
        </div>
      ))}
    </div>
  );
}

function ErrorRetry({ onRetry }: { onRetry: () => void }) {
  const t = useTranslator();
  return (
    <div className="m-2 rounded-lg border border-border bg-surface-2 p-4 text-center">
      <p className="text-sm font-medium text-fg">
        {t("search.unavailableTitle")}
      </p>
      <p className="mt-1 text-xs text-fg-muted">{t("search.fetchFailed")}</p>
      <button
        type="button"
        tabIndex={-1}
        onClick={onRetry}
        className="mt-3 rounded-md border border-border px-3 py-1.5 text-sm font-medium text-fg hover:bg-surface"
      >
        {t("search.retry")}
      </button>
    </div>
  );
}

function NoResults({
  query,
  featured,
  onClear,
  onPick,
}: {
  query: string;
  featured: FeaturedCategory[];
  onClear: () => void;
  onPick: (href: string) => void;
}) {
  const t = useTranslator();
  return (
    <div className="p-3">
      <p className="text-sm font-semibold text-fg">No results for “{query}”</p>
      <p className="mt-1 text-xs text-fg-muted">{t("search.checkSpelling")}</p>
      {featured.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {featured.map((c) => (
            <button
              key={c.href}
              type="button"
              tabIndex={-1}
              onClick={() => onPick(c.href)}
              className="rounded-full border border-border px-3 py-1 text-xs text-secondary hover:bg-surface-2"
            >
              {c.name}
            </button>
          ))}
        </div>
      )}
      <button
        type="button"
        tabIndex={-1}
        onClick={onClear}
        className="mt-3 text-sm font-medium text-secondary"
      >
        {t("search.clearSearch")}
      </button>
    </div>
  );
}
